import { useTranslation } from '@multaqa/i18n'
import { Label } from '@multaqa/ui/components/ui/label'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { TimeZoneCombobox, timeZoneOffsetLabel } from '@multaqa/ui/components/ui/time-zone-combobox'
import { useState } from 'react'
import type { CompanySettingResource } from './api'
import { useUpdateCompanySettings } from './api'
import { SettingsCard } from './company-settings-card'
import { SettingsReadOnlyGrid } from './company-settings-fields'

/**
 * The company's own time zone. Attendance, shift reminders, "today" and the
 * mobile app show and compute local time with it, so a wrong value moves
 * every punch by the offset difference.
 */
export function CompanyTimeZoneCard({
  groupId,
  companyId,
  settings,
}: {
  groupId: string
  companyId: string
  settings: CompanySettingResource
}) {
  const { t } = useTranslation()
  const update = useUpdateCompanySettings()
  const [editing, setEditing] = useState(false)
  const [timezone, setTimezone] = useState(settings.timezone)
  const reset = () => {
    setTimezone(settings.timezone)
    update.reset()
  }
  const save = () =>
    update.mutate(
      { groupId, companyId, input: { timezone } },
      {
        onSuccess: () => {
          setEditing(false)
          toast.success(t('state.saved'))
        },
      },
    )

  return (
    <SettingsCard
      testId="company-settings-timezone"
      title={t('companies.settings.groups.regional')}
      description={t('companies.settings.groups.regionalDescription')}
      editing={editing}
      pending={update.isPending}
      error={update.isError}
      onEdit={() => {
        reset()
        setEditing(true)
      }}
      onCancel={() => {
        reset()
        setEditing(false)
      }}
      onSave={save}
    >
      {editing ? (
        <div className="grid gap-1.5 sm:max-w-sm">
          <Label htmlFor="company-settings-timezone-input">
            {t('companies.settings.fields.timezone')}
          </Label>
          <TimeZoneCombobox
            id="company-settings-timezone-input"
            className="w-full"
            value={timezone}
            onValueChange={setTimezone}
            searchPlaceholder={t('timeZone.search')}
            emptyLabel={t('timeZone.empty')}
            data-testid="company-settings-timezone-input"
          />
        </div>
      ) : (
        <SettingsReadOnlyGrid
          rows={[
            [
              t('companies.settings.fields.timezone'),
              `${settings.timezone} (${timeZoneOffsetLabel(settings.timezone)})`,
            ],
          ]}
        />
      )}
    </SettingsCard>
  )
}
